import i18n from "i18next";
import { z } from "zod";
import { getAtcAfterAtsError, notificationSchema } from "../utils";
import {
  createAtcSchema,
  getAtcIncludesResourceEndTimesError,
  parseDateLikeValue,
} from "./sharedClosePortServiceSchemas";

const LANG_BASE = "vessel-call.port-services.fields";

const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;

const parsePositiveIntegerLikeValue = (value: unknown) => {
  if (value === "" || value === null || value === undefined) return undefined;

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    return POSITIVE_INTEGER_PATTERN.test(trimmedValue) ? Number(trimmedValue) : value;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const parseOptionalIntegerLikeValue = (value: unknown) => {
  if (value === "" || value === null || value === undefined) return undefined;

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const parsed = Number(trimmedValue);
    return Number.isNaN(parsed) || !Number.isInteger(parsed) ? value : parsed;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const LoadingAndOffloadingFieldsSchema = () =>
  z.object({
    numberOfPackagesHandled: z.preprocess(
      parsePositiveIntegerLikeValue,
      z
        .number({
          required_error: i18n.t("form-validation.required", {
            label: i18n.t(`${LANG_BASE}.number-of-packages-handled`),
          }),
          invalid_type_error: i18n.t("form-validation.invalid-number", {
            label: i18n.t(`${LANG_BASE}.number-of-packages-handled`),
          }),
        })
        .int(
          i18n.t("form-validation.invalid-number", {
            label: i18n.t(`${LANG_BASE}.number-of-packages-handled`),
          }),
        )
        .positive(
          i18n.t("form-validation.mandatory-min", {
            label: i18n.t(`${LANG_BASE}.number-of-packages-handled`),
            min: 1,
          }),
        ),
    ),
    spares: z.preprocess(
      parseOptionalIntegerLikeValue,
      z
        .number({
          invalid_type_error: i18n.t("form-validation.invalid-number", {
            label: i18n.t(`${LANG_BASE}.spares`),
          }),
        })
        .int(
          i18n.t("form-validation.invalid-number", {
            label: i18n.t(`${LANG_BASE}.spares`),
          }),
        )
        .optional(),
    ),
    incidents: z.string().max(2500).optional(),
    resources: z
      .array(
        z.object({
          usageStartTime: z.preprocess(parseDateLikeValue, z.date().optional()),
          usageEndTime: z.preprocess(parseDateLikeValue, z.date().optional()),
        }),
      )
      .optional(),
  });

type CloseLoadingAndOffloadingRefineResource = {
  usageEndTime?: Date;
};

export const closeLoadingAndOffloadingSchema = (ats?: string | Date | null) =>
  createAtcSchema()
    .merge(LoadingAndOffloadingFieldsSchema())
    .merge(notificationSchema)
    .superRefine((value, ctx) => {
      const atc = value.atc as Date | undefined;
      const resources = Array.isArray(value.resources)
        ? (value.resources as CloseLoadingAndOffloadingRefineResource[])
        : [];
      const error = getAtcAfterAtsError(atc, ats);

      if (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["atc"],
          message: error,
        });
      }

      const atcResourceCoverageError = getAtcIncludesResourceEndTimesError(
        atc,
        resources.map((resource) => resource.usageEndTime),
      );

      if (atcResourceCoverageError) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["atc"],
          message: atcResourceCoverageError,
        });
      }
    });

export type CloseLoadingAndOffloadingSchema = z.infer<
  ReturnType<typeof closeLoadingAndOffloadingSchema>
>;
